import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, secToHour, secToMinute, secToTwoDigitsMinute, secToSec, secToTwoDigitsSec } from '../../../utils/date'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'

interface SearchResult {
  total: {
    time: number
    biz: number
    count: number
    efficiency: {
      score: number
      judgement: string
    }
    focus: {
      score: number
      judgement: string
    }
    stress: {
      score: number
      judgement: string
    }
  },
  service: {
    icon: string
    color: string
    icon_color: string
    name: string
    url: string
    title: string
  },
  members: [
    {
      id: number
      name: string
      date: string
      biz: number
      time: number
      count: number
      efficiency: {
        score: number
        judgement: string
      }
      focus: {
        score: number
        judgement: string
      }
      stress: {
        score: number
        judgement: string
      }
    }
  ]
}

interface SearchRequestParams {
  start_date: string
  end_date: string
  team_ids: number[]
  member_ids: number[]
  keywords: string[]
  service_names: string[]
  category_ids: number[]
  efficiency_level?: string[],
  stress_level?: string[],
  focus_level?: string[],
  sort_key?: string
  sort_type?: string
}

interface Props {
  result: SearchResult
  request: SearchRequestParams | undefined
  displayGraph: boolean
}

interface DetailList {
  id: number
  name: string
  date: string
  biz: number
  time: number
  count: number
  efficiency: {
    score: number
    judgement: string
  }
  focus: {
    score: number
    judgement: string
  }
  stress: {
    score: number
    judgement: string
  }
}

type DetailListKey = "id" | "name" | "date" | "biz" | "time" | "count" | "efficiency" | "stress" | "focus"

const AppDetail: FC<Props> = (props) => {
  const {
    result,
    request,
    displayGraph,
  } = props

  const { t } = useTranslation()

  const DISPLAY_LIMIT = 8

  const PAGINATION_DISPLAY_NUM = 5

  const [displayDetail, setDisplayDetail] = useState<boolean>(false)
  const [pageOffset, setPageOffset] = useState<number>(1)
  const [pageNum, setPageNum] = useState<number>(1)
  const [detailList, setDetailList] = useState<{page:number, list:DetailList[]}[]>()

  const [sortStatus, setSortStatus] = useState<{type:string, order:string}>({ type: "", order: "" })

  const arrayChunk = ([...array], size = 1) => (
    array.reduce((acc, _value, index) => (index % size ? acc : [...acc, array.slice(index, index + size)]), [])
  )

  const pagination = () => {
    const displayNum = pageNum < PAGINATION_DISPLAY_NUM ? pageNum : PAGINATION_DISPLAY_NUM
    return (
      <div className="pagination">
        <div
          onClick={pageOffset === 1 ? undefined : () => setPageOffset(pageOffset - 1)}
          role="none"
          className={`page ${pageOffset === 1 ? "disabled" : ""}`}
        >
          &lt;
        </div>
        {
          pageOffset <= Math.ceil(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(i + 1)}
                role="none"
                className={`page ${pageOffset === i + 1 ? "selected" : ""}`}
                key={uniqueId()}
              >
                {i + 1}
              </div>
            ))
          )
        }
        {
          pageOffset > Math.ceil(displayNum / 2) && pageNum - pageOffset > Math.floor(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(pageOffset - Math.floor(displayNum / 2) + i)}
                role="none"
                className={`page ${pageOffset === pageOffset - Math.floor(displayNum / 2) + i ? "selected" : ""}`}
                key={uniqueId()}
              >
                {pageOffset - Math.floor(displayNum / 2) + i}
              </div>
            ))
          )
        }
        {
          pageOffset > Math.ceil(displayNum / 2) && pageNum - pageOffset <= Math.floor(displayNum / 2) && (
            Array(displayNum).fill(null).map((v, i) => (
              <div
                onClick={() => setPageOffset(pageNum - displayNum + 1 + i)}
                role="none"
                className={`page ${pageOffset === pageNum - displayNum + 1 + i ? "selected" : ""}`}
                key={uniqueId()}
              >
                {pageNum - displayNum + 1 + i}
              </div>
            ))
          )
        }
        <div
          onClick={pageOffset === pageNum ? undefined : () => setPageOffset(pageOffset + 1)}
          role="none"
          className={`page ${pageOffset === pageNum ? "disabled" : ""}`}
        >
          &gt;
        </div>
      </div>
    )
  }

  const sortResult = (key_name: DetailListKey, sort?: "ASC" | "DESC") => {
    if (!key_name) return
    const order = sort || "ASC"
    let line = []
    if (order === "ASC") {
      if (key_name === "efficiency" || key_name === "stress" || key_name === "focus") {
        line = [...result.members].sort((val_1, val_2) => {
          if (val_1[key_name].score < val_2[key_name].score) return -1
          if (val_1[key_name].score > val_2[key_name].score) return 1
          return 0
        })
      } else {
        line = [...result.members].sort((val_1, val_2) => {
          if (val_1[key_name] < val_2[key_name]) return -1
          if (val_1[key_name] > val_2[key_name]) return 1
          return 0
        })
      }
      setSortStatus({ type: key_name, order: "ASC" })
    } else {
      if (key_name === "efficiency" || key_name === "stress" || key_name === "focus") {
        line = [...result.members].sort((val_1, val_2) => {
          if (val_1[key_name].score < val_2[key_name].score) return 1
          if (val_1[key_name].score > val_2[key_name].score) return -1
          return 0
        })
      } else {
        line = [...result.members].sort((val_1, val_2) => {
          if (val_1[key_name] < val_2[key_name]) return 1
          if (val_1[key_name] > val_2[key_name]) return -1
          return 0
        })
      }
      setSortStatus({ type: key_name, order: "DESC" })
    }
    setDetailList(arrayChunk(line, DISPLAY_LIMIT).map((list: DetailList, key: number) => ({ page: key + 1, list: list })))
    setPageOffset(1)
  }

  const resetSortResult = () => {
    setSortStatus({ type: "", order: "" })
    setDetailList(arrayChunk(result.members, DISPLAY_LIMIT).map((list: DetailList, key: number) => ({ page: key + 1, list: list })))
    setPageOffset(1)
  }

  const sortBlock = (type: DetailListKey) => {
    const order = ((sortStatus.type !== type) || (sortStatus.type === type && sortStatus.order === 'DESC')) ? "ASC" : "DESC"
    return (
      <>
        <span
          onClick={() => sortResult(type, order)}
          role="none"
          className="sort_button"
        >
          {t(`search.result.data.table.${type}`)}
        </span>
        <div className="sort_icon_area">
          {
            sortStatus.type === type && sortStatus.order === 'DESC' && (
              <span
                onClick={() => sortResult(type, "ASC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort-amount-down']} />
              </span>
            )
          }
          {
            sortStatus.type === type && sortStatus.order === 'ASC' && (
              <span
                onClick={() => sortResult(type, "DESC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort-amount-up']} />
              </span>
            )
          }
          {
            sortStatus.type !== type && (
              <span
                onClick={() => sortResult(type, "DESC")}
                role="none"
                className="sort_button"
              >
                <FontAwesomeIcon icon={['fas', 'sort']} />
              </span>
            )
          }
        </div>
      </>
    )
  }

  const buildGraphScore = (score: number, index: number, color: string, warningColor: string) => {
    const graphColor = score < 50 ? warningColor : color
    if ((index + 1) * 10 < score) {
      return `${graphColor} 0%, ${graphColor} 100%`
    }
    if ((index + 1) * 10 >= score && (index + 1) * 10 - score < 10) {
      return `${graphColor} ${((score - (index * 10)) * 10)}%, #eee ${((score - (index * 10)) * 10)}%`
    }
    return '#eee 0%, #eee 100%'
  }

  const buildGraph = (score: number, judgement: string, type: string) => {
    let color = "#ddd"
    let warningColor = '#ddd'
    if (
      (type === "efficiency" && request?.efficiency_level?.length)
      || (type === "stress" && request?.stress_level?.length)
      || (type === "focus" && request?.focus_level?.length)
      || (!request?.efficiency_level?.length && !request?.stress_level?.length && !request?.focus_level?.length)
    ) {
      color = "#4472ba"
      warningColor = '#feb019'
    }
    return (
      <>
        <div
          style={{
            height: "55px",
            width: "100px",
            position: "relative",
          }}
        >
          {
            Array(10).fill(null).map((_, i) => (
              <div
                style={{
                  background: `linear-gradient(to right, ${buildGraphScore(score, i, color, warningColor)})`,
                  borderRight: "1px solid #fff",
                  bottom: 0,
                  height: `${i * 5 + 10}px`,
                  left: `${i * 10}%`,
                  position: "absolute",
                  width: "10px",
                  zIndex: 1,
                }}
                key={uniqueId()}
              />
            ))
          }
        </div>
        {
          judgement !== "b" && (
            <div
              style={{
                width: "100%",
                textAlign: "right",
                color: color,
              }}
            />
          )
        }
      </>
    )
  }

  const toggleDetail = () => {
    resetSortResult()
    setDisplayDetail(!displayDetail)
  }

  useEffect(() => {
    const array = arrayChunk(result.members, DISPLAY_LIMIT).map((list: DetailList, key: number) => ({ page: key + 1, list: list }))
    setDetailList(array)
    setPageNum(array.length)
  }, [])

  return (
    <div className={`detail_box ${displayDetail && 'active'}`}>
      <div className="detail_summary_wrapper">
        <div
          className={`detail_summary ${displayDetail && 'active'}`}
        >
          <div className="detail_header">
            <div className="app_icon_wrapper">
              <div
                style={{ backgroundColor: result.service.color }}
                className="app_icon"
              >
                <i
                  className={result.service.icon}
                  style={{
                    color: result.service.icon_color,
                  }}
                />
              </div>
              <div className="app_name">
                {result.service.name}
              </div>
            </div>
            <div className="app_title_wrapper">
              {
                result.service.title ? (
                  <div className="app_title">{result.service.title}</div>
                ) : (
                  <div className="app_title no_title">タイトルなし</div>
                )
              }
              <div className="app_url">
                {result.service.url}
              </div>
              <div className="detail_more_button_wrapper">
                <div
                  onClick={() => toggleDetail()}
                  role="none"
                  className={`detail_more_button ${displayDetail && 'active'}`}
                >
                  {
                    displayDetail ? (
                      <>
                        {t('search.result.data.button.close')}
                        <FontAwesomeIcon icon={['fas', 'angle-up']} className="ml4" />
                      </>
                    ) : (
                      <>
                        {t('search.result.data.button.open')}
                        <FontAwesomeIcon icon={['fas', 'angle-down']} className="ml4" />
                      </>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="detail_block time">
            <div className="value">
              {
                secToHour(result.total.time) ? (
                  <>
                    <span className="hour">
                      {secToHour(result.total.time)}
                    </span>
                    {t('search.result.hour')}
                  </>
                ) : (
                  <></>
                )
              }
              {
                secToHour(result.total.time) || secToMinute(result.total.time) !== "0" ? (
                  <>
                    <span className="minute">
                      {
                        secToHour(result.total.time)
                          ? secToTwoDigitsMinute(result.total.time)
                          : secToMinute(result.total.time)
                      }
                    </span>
                    {t('search.result.minute')}
                  </>
                ) : (
                  <></>
                )
              }
              <span className="second">
                {
                  (secToHour(result.total.time) || secToMinute(result.total.time) !== "0")
                    ? secToTwoDigitsSec(result.total.time)
                    : secToSec(result.total.time)
                }
              </span>
              {t('search.result.second')}
            </div>
          </div>
          <div className="detail_block biz">
            <div className="value">
              <span className="biz">
                {result.total.biz.toLocaleString()}
              </span>
              {t('search.result.biz')}
            </div>
          </div>
          <div className="detail_block count">
            <div className="value">
              <span className="count">
                {result.total.count.toLocaleString()}
              </span>
              {t('search.result.count')}
            </div>
          </div>
          <div className="detail_block blank" />
        </div>
        {
          displayGraph && (
            <div className={`detail_summary graph ${displayDetail && 'active'}`}>
              <div className="detail_block graph">
                <div className="value">
                  {buildGraph(result.total.efficiency.score, result.total.efficiency.judgement, "efficiency")}
                </div>
              </div>
              <div className="detail_block graph">
                <div className="value">
                  {buildGraph(result.total.stress.score, result.total.stress.judgement, "stress")}
                </div>
              </div>
              <div className="detail_block graph">
                <div className="value">
                  {buildGraph(result.total.focus.score, result.total.focus.judgement, "focus")}
                </div>
              </div>
            </div>
          )
        }
      </div>
      {
        displayDetail && (
          <>
            {
              result.service.title ? (
                <div className="detail_app_title">{result.service.title}</div>
              ) : (
                <div className="detail_app_title no_title">タイトルなし</div>
              )
            }
            <div className="detail_app_url">{result.service.url}</div>
            <table className={`flex-table search_result_detail ${pageNum > 1 && 'pager'} ${displayGraph || "not_ai"}`}>
              <thead>
                <tr>
                  <th>
                    {sortBlock('date')}
                  </th>
                  <th>
                    {sortBlock('name')}
                  </th>
                  <th>
                    {sortBlock('time')}
                  </th>
                  <th>
                    {sortBlock('biz')}
                  </th>
                  <th>
                    {sortBlock('count')}
                  </th>
                  {
                    displayGraph && (
                      <>
                        <th className="blank_title" aria-label="余白" />
                        <th>
                          {sortBlock('efficiency')}
                        </th>
                        <th>
                          {sortBlock('stress')}
                          <span className="detail_table_title" aria-label="table title">
                            {t('search.result.data.table.appropriateness')}
                          </span>
                        </th>
                        <th>
                          {sortBlock('focus')}
                        </th>
                      </>
                    )
                  }
                </tr>
              </thead>
              <tbody>
                {
                  detailList?.find((value) => value.page === pageOffset)?.list.map((value) => (
                    <tr key={`detail_${value.date}_${value.name}_${value}_${uniqueId()}`}>
                      <td className="no_trans">
                        {format(value.date, "M月D日")}
                      </td>
                      <td className="no_trans">
                        {value.name}
                      </td>
                      <td>
                        <div>
                          {
                            secToHour(value.time) ? (
                              <>
                                <span className="hour">
                                  {secToHour(value.time)}
                                </span>
                                {t('search.result.hour')}
                              </>
                            ) : (
                              <></>
                            )
                          }
                          {
                            secToHour(value.time) || secToMinute(value.time) !== "0" ? (
                              <>
                                <span className="minute">
                                  {
                                    secToHour(value.time)
                                      ? secToTwoDigitsMinute(value.time)
                                      : secToMinute(value.time)
                                  }
                                </span>
                                {t('search.result.minute')}
                              </>
                            ) : (
                              <></>
                            )
                          }
                          <span className="second">
                            {
                              (secToHour(value.time) || secToMinute(value.time) !== "0")
                                ? secToTwoDigitsSec(value.time)
                                : secToSec(value.time)
                            }
                          </span>
                          {t('search.result.second')}
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="biz">
                            {value.biz.toLocaleString()}
                          </span>
                          {t('search.result.biz')}
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="count">
                            {value.count.toLocaleString()}
                          </span>
                          {t('search.result.count')}
                        </div>
                      </td>
                      {
                        displayGraph && (
                          <>
                            <td />
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#0A4E8D",
                                  fontSize: "12px",
                                  flexFlow: "column",
                                  width: "80%",
                                }}
                              >
                                {buildGraph(value.efficiency.score, value.efficiency.judgement, "efficiency")}
                              </div>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#0A4E8D",
                                  fontSize: "12px",
                                  flexFlow: "column",
                                  width: "80%",
                                }}
                              >
                                {buildGraph(value.stress.score, value.stress.judgement, "stress")}
                              </div>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#0A4E8D",
                                  fontSize: "12px",
                                  flexFlow: "column",
                                  width: "80%",
                                }}
                              >
                                {buildGraph(value.focus.score, value.focus.judgement, "focus")}
                              </div>
                            </td>
                          </>
                        )
                      }
                    </tr>
                  ))
                }
              </tbody>
            </table>
            {
              pageNum > 1 && (
                pagination()
              )
            }
          </>
        )
      }
    </div>
  )
}

export default AppDetail
